import { useEffect, useMemo, useState } from 'react';
import { usePickupRecipients } from '../../features/org-orders/api';
import { EMAIL_PATTERN, dedupeEmails, parseEmailList } from './bolRecipients';

/**
 * Pick who gets a load's pickup-instructions email. The master customer and
 * the sub-users with access to the load's delivery location start checked;
 * other active sub-users are listed so they can be added deliberately. Anyone
 * who turned pickup emails off is shown but can't be selected. Extra addresses
 * can be typed in, comma separated; they're appended and can stand alone.
 *
 * Rendered inside OrgOrderRow, so it shares the BOL dialog's recipient styles.
 */
export function PickupRecipientsDialog({
  orderId,
  itemId,
  onCancel,
  onConfirm,
  isSubmitting,
}: {
  orderId: string;
  itemId: string;
  onCancel: () => void;
  onConfirm: (recipients: string[], extraRecipients: string[]) => void;
  isSubmitting?: boolean;
}) {
  const { data: options = [], isLoading, isError } = usePickupRecipients(orderId, itemId);

  const [selected, setSelected] = useState<string[]>([]);
  const [extraEmails, setExtraEmails] = useState('');

  useEffect(() => {
    setSelected(options.filter((option) => option.selected).map((option) => option.email));
  }, [options]);

  const toggle = (email: string) =>
    setSelected((current) =>
      current.includes(email) ? current.filter((entry) => entry !== email) : [...current, email]
    );

  const selectable = options.filter((option) => !option.optedOut);

  const optedOutCount = options.length - selectable.length;

  const allSelected = selectable.length > 0 && selected.length === selectable.length;

  const extras = useMemo(() => parseEmailList(extraEmails), [extraEmails]);
  const invalidExtras = useMemo(
    () => extras.filter((entry) => !EMAIL_PATTERN.test(entry)),
    [extras]
  );

  // Typed addresses that match someone who opted out would be rejected anyway.
  const optedOutExtras = useMemo(() => {
    const optedOut = new Set(
      options.filter((option) => option.optedOut).map((option) => option.email.toLowerCase())
    );
    return extras.filter((entry) => optedOut.has(entry.toLowerCase()));
  }, [extras, options]);

  // Deduped the same way the server does, so the count is what actually goes out.
  const total = useMemo(() => dedupeEmails([...selected, ...extras]).length, [selected, extras]);

  const canSend =
    total > 0 && invalidExtras.length === 0 && optedOutExtras.length === 0 && !isSubmitting;

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={onCancel} />
      <div className="modal__panel">
        <div className="modal__header">
          <h3 className="modal__title">Email pickup instructions</h3>
          <div className="muted tiny bol-carrier-subtitle">
            The customer and this location&apos;s users are selected by default.
          </div>
        </div>

        <div className="modal__body">
          <div className="form-control">
            <div className="bol-recipients__heading">
              <label>Recipients *</label>

              {selectable.length > 1 && (
                <button
                  type="button"
                  className="btn btn--link bol-recipients__select-all"
                  onClick={() => setSelected(allSelected ? [] : selectable.map((o) => o.email))}
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="muted tiny bol-recipients__empty">Loading recipients…</div>
            ) : isError ? (
              <div className="error-box error-box--spaced">Could not load recipients.</div>
            ) : options.length === 0 ? (
              <div className="muted tiny bol-recipients__empty">
                This customer has no email on file and no active users.
              </div>
            ) : (
              <ul className="bol-recipients">
                {options.map((option) => (
                  <li
                    key={option.email}
                    className={`bol-recipient${option.optedOut ? ' bol-recipient--opted-out' : ''}`}
                    title={
                      option.optedOut
                        ? 'Turned off pickup emails in their notification settings.'
                        : undefined
                    }
                  >
                    <label className="bol-recipient__label">
                      <input
                        type="checkbox"
                        checked={selected.includes(option.email)}
                        onChange={() => toggle(option.email)}
                        disabled={option.optedOut}
                      />
                      <span className="bol-recipient__body">
                        <span className="bol-recipient__name">
                          {option.name || option.email}
                          <span className="bol-recipient__type">
                            {option.kind === 'CUSTOMER' ? 'Customer' : 'User'}
                          </span>
                          {option.atLocation && (
                            <span className="bol-recipient__type bol-recipient__type--assigned">
                              This location
                            </span>
                          )}
                          {option.optedOut && (
                            <span className="bol-recipient__type">Pickup emails off</span>
                          )}
                        </span>
                        {option.name && (
                          <span className="bol-recipient__email">{option.email}</span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}

            <input
              id="pickup-extra-emails"
              type="text"
              className="bol-extra-emails"
              placeholder="someone@example.com, someone.else@example.com"
              value={extraEmails}
              onChange={(e) => setExtraEmails(e.target.value)}
              aria-label="Additional recipients"
              aria-describedby="pickup-extra-emails-help"
              aria-invalid={invalidExtras.length > 0 || optedOutExtras.length > 0}
            />
            <div id="pickup-extra-emails-help" className="muted tiny bol-extra-emails__help">
              Add other addresses, separated by commas.
            </div>

            {invalidExtras.length > 0 && (
              <div className="error-box error-box--spaced">
                Check {invalidExtras.length === 1 ? 'this address' : 'these addresses'}:{' '}
                {invalidExtras.join(', ')}
              </div>
            )}

            {optedOutExtras.length > 0 && (
              <div className="error-box error-box--spaced">
                Turned pickup emails off: {optedOutExtras.join(', ')}
              </div>
            )}
          </div>

          {optedOutCount > 0 && (
            <div className="muted tiny bol-recipients__summary">
              {optedOutCount === 1
                ? '1 person has turned pickup emails off and won’t be emailed.'
                : `${optedOutCount} people have turned pickup emails off and won’t be emailed.`}
            </div>
          )}

          <div className="muted tiny bol-recipients__summary">
            {total === 0
              ? 'Select or type at least one recipient.'
              : `Each recipient gets their own copy — ${total} ${
                  total === 1 ? 'email' : 'emails'
                } will be sent.`}
          </div>
        </div>

        <div className="modal__footer">
          <div className="actions bol-modal-actions">
            <button
              className="btn btn--primary"
              onClick={() => canSend && onConfirm(selected, extras)}
              disabled={!canSend}
            >
              {isSubmitting ? 'Sending…' : total > 1 ? `Send to ${total} recipients` : 'Send'}
            </button>
            <button className="btn btn--ghost" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
